/**
 * KartotekiSection — zwijana sekcja „Kartoteki do użycia w menu" z chipami modułów
 * i filtrem checkboxami po module źródłowym.
 *
 * Wyekstrahowana z MenuLayoutGallery (2026-07-02), żeby ta sama sekcja mogła żyć
 * w dwóch miejscach: pełnej galerii (ekran wejścia) i szynie kontekstu obok edytora
 * (MenuBuilderContextRail). Stan zwinięcia/filtra jest lokalny per instancja —
 * galeria i szyna nie renderują się jednocześnie, więc testidy się nie dublują.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MenuSourceKartoteka } from '@saas/shared';
import { ChevronDown } from 'lucide-react';

export interface KartotekiSectionProps {
  kartoteki: MenuSourceKartoteka[];
  i18nPrefix: string;
}

export function KartotekiSection({ kartoteki, i18nPrefix }: KartotekiSectionProps) {
  const { t } = useTranslation();

  // Kartoteki: sekcja zwijana (domyslnie schowana — lista rosnie z liczba modulow)
  // + filtr checkboxami po module zrodlowym.
  const [kartotekiOpen, setKartotekiOpen] = useState(false);
  const [moduleFilter, setModuleFilter] = useState<Set<string>>(new Set());
  const kartotekiModules = useMemo(() => {
    const map = new Map<string, string>();
    for (const k of kartoteki)
      for (const m of k.usedInModules) if (!map.has(m.code)) map.set(m.code, m.name);
    return [...map.entries()].map(([code, name]) => ({ code, name }));
  }, [kartoteki]);
  const filteredKartoteki = useMemo(() => {
    if (moduleFilter.size === 0) return kartoteki;
    return kartoteki.filter((k) => k.usedInModules.some((m) => moduleFilter.has(m.code)));
  }, [kartoteki, moduleFilter]);
  const toggleModuleFilter = (code: string) =>
    setModuleFilter((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  if (kartoteki.length === 0) return null;

  return (
    <section data-testid="menu-gallery-kartoteki">
      <button
        type="button"
        onClick={() => setKartotekiOpen((o) => !o)}
        aria-expanded={kartotekiOpen}
        className="flex items-center gap-2 rounded-md border border-border-secondary bg-bg-elevated px-3 py-2 text-xs font-semibold uppercase tracking-wider text-text-muted transition hover:border-accent-300 hover:text-text-secondary"
        data-testid="menu-gallery-kartoteki-toggle"
      >
        {t(`${i18nPrefix}.gallery.kartotekiHeader`, {
          defaultValue: 'Kartoteki do użycia w menu',
        })}
        <span className="rounded-full bg-bg-tertiary px-1.5 py-0.5 text-[10px]">
          {kartoteki.length}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${kartotekiOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {kartotekiOpen ? (
        <div className="mt-2 rounded-lg border border-border-secondary bg-bg-elevated p-3">
          <p className="text-xs text-text-secondary">
            {t(`${i18nPrefix}.gallery.kartotekiHint`, {
              defaultValue:
                'Z nich składasz pozycje w edytorze — chip pokazuje, z którego modułu pochodzi.',
            })}
          </p>
          {kartotekiModules.length > 1 ? (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5">
              {kartotekiModules.map((m) => (
                <label
                  key={m.code}
                  className="flex cursor-pointer items-center gap-1.5 text-xs text-text-secondary"
                >
                  <input
                    type="checkbox"
                    checked={moduleFilter.has(m.code)}
                    onChange={() => toggleModuleFilter(m.code)}
                    data-testid={`menu-gallery-kartoteki-filter-${m.code}`}
                  />
                  {m.name}
                </label>
              ))}
            </div>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-1.5">
            {filteredKartoteki.map((k) => (
              <span
                key={`${k.kind}-${k.slug}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-border-secondary bg-bg-primary px-2.5 py-1 text-xs text-text-primary"
                data-testid={`menu-gallery-kartoteka-${k.slug}`}
              >
                {k.label}
                {(k.usedInModules.length > 0
                  ? k.usedInModules.map((m) => m.name)
                  : [
                      t(`${i18nPrefix}.palette.kartotekaOwnChip`, {
                        defaultValue: 'własna',
                      }),
                    ]
                ).map((chip) => (
                  <span
                    key={chip}
                    className="rounded-full bg-bg-tertiary px-1.5 py-0.5 text-[10px] text-text-muted"
                  >
                    {chip}
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
